'use client';
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Mail, Phone, MapPin, Send, User, Building2, ArrowLeft } from "lucide-react";
import { CardTitle, Card, CardContent, CardHeader, CardDescription } from "@/components/ui/Card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/Select";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import { useUser } from "@clerk/nextjs";


export default function ContactForm() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { isLoaded, isSignedIn, user } = useUser();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company: "",
        subject: searchParams.get('subject') || "",
        message: "",
    });

    useEffect(() => {
        if (!isLoaded || !isSignedIn || !user) return;

        const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
        const primaryEmail = user.primaryEmailAddress?.emailAddress ?? "";

        setFormData((prev) => ({
            ...prev,
            name: prev.name.trim() ? prev.name : fullName,
            email: prev.email.trim() ? prev.email : primaryEmail,
        }));
    }, [isLoaded, isSignedIn, user?.id]);

    useEffect(() => {
        if (!isLoaded || !isSignedIn) return;

        const prefillCompany = async () => {
            try {
                const response = await fetch("/api/user/company");
                if (!response.ok) return;

                const data = (await response.json()) as {
                    success: boolean;
                    customer?: { name?: string | null } | null;
                };

                const companyName = data.customer?.name?.trim() ?? "";
                if (!companyName) return;

                setFormData((prev) => ({
                    ...prev,
                    company: prev.company.trim() ? prev.company : companyName,
                }));
            } catch {
                // On laisse le champ manuel en cas d'erreur réseau/API.
            }
        };

        void prefillCompany();
    }, [isLoaded, isSignedIn]);

    const [errors, setErrors] = useState<Record<string, string>>(
        {},
    );

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom est requis";
        }

        if (!formData.email.trim()) {
            newErrors.email = "L'email est requis";
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ) {
            newErrors.email = "Email invalide";
        }

        if (!formData.company.trim()) {
            newErrors.company = "L'entreprise est requise";
        }

        if (!formData.subject.trim()) {
            newErrors.subject = "Le sujet est requis";
        }

        if (!formData.message.trim()) {
            newErrors.message = "Le message est requis";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error("Erreur serveur");
            }

            toast.success(
                "Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.",
            );

            setFormData({
                name: "",
                email: "",
                company: "",
                subject: "",
                message: "",
            });
        } catch (error) {
            console.error("Erreur lors de l'envoi:", error);
            toast.error(
                "Une erreur est survenue. Veuillez réessayer.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (field: string, value: string) => {
        setFormData({ ...formData, [field]: value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: "" });
        }
    };
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                <div className="lg:col-span-1 space-y-6">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            Contactez-nous
                        </h2>
                        <p className="text-gray-600">
                            Notre équipe est à votre disposition pour
                            répondre à toutes vos questions
                        </p>
                    </div>

                    <div className="space-y-4">
                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-start gap-4">
                                    <div className="bg-blue-100 rounded-lg p-3">
                                        <Phone className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">
                                            Téléphone
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            +33 3 85 47 89 00
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-start gap-4">
                                    <div className="bg-blue-100 rounded-lg p-3">
                                        <MapPin className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900 mb-1">
                                            Adresse
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Rue de l&apos;Argentique,
                                            <br />
                                            71530, Fragnes-La Loyère
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-4">
                            <h3 className="font-semibold text-blue-900 mb-3">
                                Horaires d&apos;ouverture
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-blue-700">
                                        Lundi - Vendredi
                                    </span>
                                    <span className="font-medium text-blue-900">
                                        8h - 18h
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-blue-700">
                                        Week-End
                                    </span>
                                    <span className="font-medium text-blue-900">
                                        Fermé
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:col-span-2 space-y-4">
                    <div>
                        <Button
                            variant="ghost"
                            onClick={() => router.push("/")}
                            className="gap-2 -ml-4 text-gray-600 hover:text-gray-900"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Retour à l&apos;accueil
                        </Button>
                    </div>
                    <Card>
                        <CardHeader>
                            <CardTitle>Envoyez-nous un message</CardTitle>
                            <CardDescription>
                                Remplissez le formulaire ci-dessous et nous
                                vous répondrons dans les plus brefs délais
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form
                                onSubmit={handleSubmit}
                                className="space-y-6"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Name */}
                                    <div>
                                        <Label
                                            htmlFor="name"
                                            className="text-sm font-medium text-gray-700 mb-2 block"
                                        >
                                            Nom complet{" "}
                                            <span className="text-red-500">*</span>
                                        </Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                id="name"
                                                type="text"
                                                placeholder="Jean Dupont"
                                                className={`pl-10 ${errors.name ? "border-red-500" : ""}`}
                                                value={formData.name}
                                                onChange={(e) =>
                                                    handleChange("name", e.target.value)
                                                }
                                            />
                                        </div>
                                        {errors.name && (
                                            <p className="text-xs text-red-500 mt-1">
                                                {errors.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <Label
                                            htmlFor="email"
                                            className="text-sm font-medium text-gray-700 mb-2 block"
                                        >
                                            Email{" "}
                                            <span className="text-red-500">*</span>
                                        </Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="jean.dupont@example.com"
                                                className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                                                value={formData.email}
                                                onChange={(e) =>
                                                    handleChange(
                                                        "email",
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                        {errors.email && (
                                            <p className="text-xs text-red-500 mt-1">
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Company */}
                                <div>
                                    <Label
                                        htmlFor="company"
                                        className="text-sm font-medium text-gray-700 mb-2 block"
                                    >
                                        Entreprise{" "}
                                        <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            id="company"
                                            type="text"
                                            placeholder="Nom de votre entreprise"
                                            className={`pl-10 ${errors.company ? "border-red-500" : ""}`}
                                            value={formData.company}
                                            onChange={(e) =>
                                                handleChange(
                                                    "company",
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>
                                    {errors.company && (
                                        <p className="text-xs text-red-500 mt-1">
                                            {errors.company}
                                        </p>
                                    )}
                                </div>

                                {/* Subject */}
                                <div>
                                    <Label
                                        htmlFor="subject"
                                        className="text-sm font-medium text-gray-700 mb-2 block"
                                    >
                                        Sujet{" "}
                                        <span className="text-red-500">*</span>
                                    </Label>
                                    <Select
                                        value={formData.subject}
                                        onValueChange={(value) =>
                                            handleChange("subject", value)
                                        }
                                    >
                                        <SelectTrigger
                                            className={
                                                errors.subject ? "border-red-500" : ""
                                            }
                                        >
                                            <SelectValue placeholder="Sélectionnez un sujet" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="devis">
                                                Demande de devis
                                            </SelectItem>
                                            <SelectItem value="maintenance">
                                                Maintenance
                                            </SelectItem>
                                            <SelectItem value="analyse">
                                                Analyse d&apos;huile
                                            </SelectItem>
                                            <SelectItem value="pieces">
                                                Commande de pièces détachées
                                            </SelectItem>
                                            <SelectItem value="formation">
                                                Formation technique
                                            </SelectItem>
                                            <SelectItem value="commerciale">
                                                Demande commerciale
                                            </SelectItem>
                                            <SelectItem value="autre">
                                                Autre demande
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {errors.subject && (
                                        <p className="text-xs text-red-500 mt-1">
                                            {errors.subject}
                                        </p>
                                    )}
                                </div>

                                {/* Message */}
                                <div>
                                    <Label
                                        htmlFor="message"
                                        className="text-sm font-medium text-gray-700 mb-2 block"
                                    >
                                        Message{" "}
                                        <span className="text-red-500">*</span>
                                    </Label>
                                    <textarea
                                        id="message"
                                        rows={6}
                                        placeholder="Décrivez votre demande en détail..."
                                        className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.message
                                            ? "border-red-500"
                                            : "border-gray-300"
                                            }`}
                                        value={formData.message}
                                        onChange={(e) =>
                                            handleChange("message", e.target.value)
                                        }
                                    />
                                    {errors.message && (
                                        <p className="text-xs text-red-500 mt-1">
                                            {errors.message}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 gap-2"
                                >
                                    <Send className="h-4 w-4" />
                                    {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}